import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { onboardStart, onboardComplete, fetchScarcity, fetchFoundingCount, checkoutStart } from "../lib/api";
import { RI_CITIES } from "../lib/ri-cities";
import { TRADES } from "../lib/trades";

const RI_CITIES_WITH_OTHER = [...RI_CITIES, "Other RI City"];


const TOTAL_STEPS = 5;

const OnboardSimple = () => {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    owner_name: "", phone: "", city: "Providence", trade: "plumber",
    business_name: "", zip_code: "", travel_radius_miles: 10, avg_job_value: 200,
    email: "", has_website_before_nearnow: false,
  });
  const [contractorId, setContractorId] = useState(null);
  const [foundingNumber, setFoundingNumber] = useState(null);
  const [isFounding, setIsFounding] = useState(false);
  const [scarcity, setScarcity] = useState(null);
  const [founding, setFounding] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetchFoundingCount().then(setFounding).catch(() => {});
  }, []);

  useEffect(() => {
    if (step >= 2 && data.city && data.trade) {
      fetchScarcity(data.city, data.trade).then(setScarcity).catch(() => {});
    }
  }, [step, data.city, data.trade]);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const next = async () => {
    setError(null);
    if (step === 1) {
      if (!data.owner_name.trim() || !data.phone.trim()) {
        setError("Please tell us your name and phone number.");
        return;
      }
      setBusy(true);
      try {
        const refCode = (new URLSearchParams(window.location.search).get("ref") || "").trim().toUpperCase();
        const res = await onboardStart({
          owner_name: data.owner_name, phone: data.phone, city: data.city, trade: data.trade,
          ...(refCode ? { referral_code: refCode } : {}),
        });
        setContractorId(res.contractor_id);
        setIsFounding(!!res.is_founding);
        setFoundingNumber(res.founding_number);
        setStep(2);
      } catch (e) {
        setError("Something went wrong — we will fix this. Call us: 401-555-0100");
      } finally {
        setBusy(false);
      }
    } else if (step === 4) {
      setBusy(true);
      try {
        await onboardComplete({
          contractor_id: contractorId,
          business_name: data.business_name || null,
          zip_code: data.zip_code || null,
          travel_radius_miles: Number(data.travel_radius_miles) || 10,
          avg_job_value: Number(data.avg_job_value) || 200,
          has_website_before_nearnow: data.has_website_before_nearnow,
        });
        setStep(5);
      } catch (e) {
        setError("Something went wrong — we will fix this. Call us: 401-555-0100");
      } finally {
        setBusy(false);
      }
    } else {
      setStep((s) => s + 1);
    }
  };

  const skip = () => {
    setError(null);
    if (step === 4) {
      next();
    } else {
      setStep((s) => Math.min(s + 1, TOTAL_STEPS));
    }
  };

  const finish = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!data.email) {
        setError("Email is required to send your receipt.");
        setBusy(false);
        return;
      }
      const res = await checkoutStart({
        contractor_id: contractorId, plan: "v2_starter", email: data.email, is_founding: isFounding,
      });
      // Real Stripe: redirect to hosted Checkout page. User returns to /welcome via success_url.
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      // Fallback (only hit if Stripe ever returns mock=true again)
      nav(`/welcome?session=${res.session_id}&founding=${isFounding ? 1 : 0}&number=${foundingNumber || ""}`);
    } catch (e) {
      setError("Card capture failed. Try again or call 401-555-0100.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="onboard-simple-page" style={{ maxWidth: 720, margin: "0 auto", padding: "32px 20px 80px" }}>
      {/* Header */}
      <div style={{ marginBottom: 22 }}>
        <p style={{ color: "#5a5d66", fontSize: 15, margin: 0, letterSpacing: 1 }}>
          STEP {step} OF {TOTAL_STEPS} · ABOUT {Math.max(1, 6 - step)} MIN LEFT
        </p>
        <div className="nn-progress-track" style={{ marginTop: 8 }} data-testid="progress-bar">
          <div className="nn-progress-fill" style={{ width: `${(step / TOTAL_STEPS) * 100}%` }} />
        </div>
      </div>

      {/* Step 1 */}
      {step === 1 && (
        <div data-testid="step-1" className="nn-card">
          <h1 className="font-display" style={{ fontSize: 52, margin: "0 0 8px", color: "#0A1628" }}>
            WHO ARE YOU?
          </h1>
          <p style={{ fontSize: 18, color: "#5a5d66", marginTop: 0 }}>
            Just three quick things. Takes less than a minute.
          </p>
          {founding && founding.remaining > 0 && (
            <div data-testid="founding-pill" style={pillStyle}>
              <strong>{founding.remaining}</strong> FOUNDING SLOTS LEFT · FIRST MONTH FREE
            </div>
          )}

          <label className="nn-label" style={{ marginTop: 20 }}>Your name</label>
          <input
            data-testid="onboard-owner-name"
            className="nn-input" placeholder="Joe Plumber" autoFocus
            value={data.owner_name} onChange={(e) => update("owner_name", e.target.value)}
          />

          <label className="nn-label" style={{ marginTop: 16 }}>Your phone number</label>
          <input
            data-testid="onboard-phone"
            className="nn-input" placeholder="(401) 555-0100" type="tel" inputMode="tel"
            value={data.phone} onChange={(e) => update("phone", e.target.value)}
          />

          <label className="nn-label" style={{ marginTop: 16 }}>What do you do?</label>
          <select
            data-testid="onboard-trade"
            className="nn-input" value={data.trade}
            onChange={(e) => update("trade", e.target.value)}
          >
            {TRADES.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
          </select>

          <label className="nn-label" style={{ marginTop: 16 }}>What town?</label>
          <select
            data-testid="onboard-city"
            className="nn-input" value={data.city}
            onChange={(e) => update("city", e.target.value)}
          >
            {RI_CITIES_WITH_OTHER.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}

      {/* Step 2 — SCARCITY HOOK */}
      {step === 2 && (
        <div data-testid="step-2" className="nn-card" style={{ background: "#0A1628", color: "#fff" }}>
          <p style={{ color: "#D4AF37", letterSpacing: 4, fontSize: 14, fontFamily: "Bebas Neue, sans-serif" }}>
            REAL TIME DEMAND IN YOUR AREA
          </p>
          <h1 className="font-display" data-testid="scarcity-headline" style={{ fontSize: 56, lineHeight: 1.02, margin: "8px 0 16px", color: "#D4AF37" }}>
            {scarcity?.count || "—"} HOMEOWNERS<br />IN {data.city.toUpperCase()}<br />NEED A {data.trade.toUpperCase()} LIKE YOU.
          </h1>
          <p style={{ fontSize: 19, color: "#cbd0db" }}>
            We're going to put your business in front of every one of them — for free for your first month.
          </p>

          {isFounding && (
            <div
              data-testid="founding-confirm"
              style={{
                marginTop: 18, padding: "16px 18px", borderRadius: 6,
                background: "rgba(212,175,55,0.18)", border: "2px solid #D4AF37",
              }}
            >
              <strong className="font-display" style={{ fontSize: 24, color: "#D4AF37", letterSpacing: 1 }}>
                YOU'RE FOUNDING CONTRACTOR #{foundingNumber}
              </strong>
              <p style={{ margin: "6px 0 0", fontSize: 16 }}>
                First month free. Permanent founding badge. Locked-in pricing forever.
              </p>
            </div>
          )}
          <p style={{ fontSize: 14, color: "#8a8d96", marginTop: 14 }}>
            Tap NEXT — we just need a few more things to get you live.
          </p>
        </div>
      )}

      {/* Step 3 — Business basics (all optional) */}
      {step === 3 && (
        <div data-testid="step-3" className="nn-card">
          <h1 className="font-display" style={{ fontSize: 48, margin: "0 0 8px", color: "#0A1628" }}>
            YOUR BUSINESS
          </h1>
          <p style={{ fontSize: 18, color: "#5a5d66", marginTop: 0 }}>
            All of this is optional. You can skip any of it.
          </p>

          <label className="nn-label" style={{ marginTop: 18 }}>Business name (optional)</label>
          <input
            data-testid="onboard-business-name"
            className="nn-input"
            placeholder="e.g. Joe's Plumbing & Heating"
            value={data.business_name}
            onChange={(e) => update("business_name", e.target.value)}
          />

          <label className="nn-label" style={{ marginTop: 16 }}>ZIP code (optional)</label>
          <input
            data-testid="onboard-zip"
            className="nn-input" placeholder="02903" inputMode="numeric"
            value={data.zip_code} onChange={(e) => update("zip_code", e.target.value)}
          />

          <label className="nn-label" style={{ marginTop: 16 }}>Do you have a website right now?</label>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              data-testid="onboard-no-website"
              className={data.has_website_before_nearnow ? "btn-outline-navy tap" : "btn-navy tap"}
              onClick={() => update("has_website_before_nearnow", false)}
              style={{ flex: 1 }}
            >
              NO
            </button>
            <button
              data-testid="onboard-yes-website"
              className={data.has_website_before_nearnow ? "btn-navy tap" : "btn-outline-navy tap"}
              onClick={() => update("has_website_before_nearnow", true)}
              style={{ flex: 1 }}
            >
              YES
            </button>
          </div>
        </div>
      )}

      {/* Step 4 — Service area */}
      {step === 4 && (
        <div data-testid="step-4" className="nn-card">
          <h1 className="font-display" style={{ fontSize: 48, margin: "0 0 8px", color: "#0A1628" }}>
            HOW FAR DO YOU TRAVEL?
          </h1>
          <p style={{ fontSize: 18, color: "#5a5d66", marginTop: 0 }}>
            We'll only send you jobs in your area.
          </p>

          <label className="nn-label" style={{ marginTop: 18 }}>Miles from {data.city}</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[5, 10, 15, 25, 50].map((m) => (
              <button
                key={m}
                data-testid={`radius-${m}`}
                className={Number(data.travel_radius_miles) === m ? "btn-navy tap" : "btn-outline-navy tap"}
                onClick={() => update("travel_radius_miles", m)}
                style={{ flex: "1 1 80px" }}
              >
                {m} MI
              </button>
            ))}
          </div>

          <label className="nn-label" style={{ marginTop: 18 }}>Average job size? (helps us send you the right work)</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[100, 250, 500, 1500].map((v) => (
              <button
                key={v}
                data-testid={`job-${v}`}
                className={Number(data.avg_job_value) === v ? "btn-navy tap" : "btn-outline-navy tap"}
                onClick={() => update("avg_job_value", v)}
                style={{ flex: "1 1 110px" }}
              >
                ${v}{v === 1500 ? "+" : ""}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Step 5 — CC capture (mock) */}
      {step === 5 && (
        <div data-testid="step-5" className="nn-card">
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
            <img src="/nearnow-icon.png" alt="" style={{ width: 56, height: 56, flexShrink: 0 }} />
            <h1 className="font-display" style={{ fontSize: 40, margin: 0, color: "#0A1628", lineHeight: 1 }}>
              YOU'RE ONE STEP AWAY.
            </h1>
          </div>
          <p style={{ fontSize: 19, color: "#1A1A2E", marginTop: 0 }}>
            First month is <strong>FREE</strong>. We need a card on file so you don't lose your spot.
            We will <strong>not</strong> charge today. Cancel anytime.
          </p>

          <div data-testid="plan-summary" style={planBox}>
            <div className="font-display" style={{ fontSize: 30, color: "#0A1628", letterSpacing: 1 }}>
              NEARNOW AUTOPILOT {isFounding ? "· FOUNDING" : ""}
            </div>
            <div style={{ fontSize: 13, color: "#6B5300", fontFamily: "Bebas Neue, sans-serif", letterSpacing: 1.5, marginTop: 4 }}>
              ⚡ INCLUDES ARIA · YOUR AI RECEPTIONIST
            </div>
            <div style={{ fontSize: 32, fontWeight: 700, marginTop: 6 }}>
              {isFounding ? "$0 FOR MONTH 1" : "$97 / month"}
            </div>
            <div style={{ fontSize: 16, color: "#5a5d66", marginTop: 4 }}>
              {isFounding ? "Then $97 / month at day 31. Cancel anytime." : "Cancel anytime."}
            </div>
          </div>

          <label className="nn-label" style={{ marginTop: 20 }}>Email for receipts</label>
          <input
            data-testid="onboard-email"
            className="nn-input" placeholder="you@example.com" type="email"
            value={data.email} onChange={(e) => update("email", e.target.value)}
          />

          <div data-testid="cc-mock-box" style={mockCardBox}>
            <span style={{
              display: "inline-block", padding: "3px 9px", borderRadius: 999,
              background: "#e6f5ec", color: "#1A7A4A", fontSize: 12,
              fontFamily: "Bebas Neue, sans-serif", letterSpacing: 1.5,
            }}>● LIVE STRIPE CHECKOUT</span>
            <p style={{ margin: "8px 0 0", fontSize: 15 }}>
              You'll be securely redirected to Stripe to enter your card.
              We don't charge today — only at day 31 if you stay.
            </p>
          </div>

          <button
            data-testid="onboard-finish"
            className="btn-gold tap"
            style={{ width: "100%", marginTop: 22, fontSize: 26 }}
            disabled={busy || !data.email}
            onClick={finish}
          >
            {busy ? "ONE SECOND..." : isFounding ? "START FREE MONTH — CARD REQUIRED" : "START — CARD REQUIRED"}
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div data-testid="onboard-error" style={{ background: "#fde7e7", color: "#A32D2D", padding: 14, borderRadius: 6, marginTop: 18, fontSize: 16 }}>
          {error}
        </div>
      )}

      {/* Nav buttons */}
      {step < 5 && (
        <div style={{ display: "flex", gap: 12, marginTop: 20, alignItems: "center" }}>
          <button
            data-testid="onboard-next"
            className="btn-gold tap"
            onClick={next}
            disabled={busy}
            style={{ flex: 1 }}
          >
            {busy ? "..." : "NEXT →"}
          </button>
          {step >= 3 && (
            <button
              data-testid="onboard-skip"
              className="btn-outline-navy tap"
              onClick={skip}
              disabled={busy}
            >
              SKIP FOR NOW
            </button>
          )}
        </div>
      )}
      {step > 1 && step < 5 && (
        <button
          data-testid="onboard-back"
          onClick={() => setStep((s) => Math.max(1, s - 1))}
          style={{ marginTop: 14, background: "transparent", border: "none", color: "#5a5d66", fontSize: 16, cursor: "pointer", textDecoration: "underline" }}
        >
          ← Back
        </button>
      )}

      <p style={{ marginTop: 28, fontSize: 14, color: "#8a8d96", textAlign: "center" }}>
        Stuck? Call us at <strong>401-555-0100</strong>. A real human will help.
      </p>
    </div>
  );
};

const pillStyle = {
  display: "inline-block", marginTop: 14, padding: "10px 14px", borderRadius: 999,
  background: "rgba(212,175,55,0.15)", border: "1px solid #D4AF37", color: "#6B5300",
  fontSize: 15, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 1.5,
};
const planBox = {
  marginTop: 16, padding: "20px 22px", borderRadius: 8, background: "#F8F9FA",
  border: "2px solid #D4AF37",
};
const mockCardBox = {
  marginTop: 16, padding: "14px 16px", borderRadius: 6, background: "#FFF8DC",
  border: "1px dashed #D4AF37",
};

export default OnboardSimple;
